import React from "react";
import * as Sentry from "@sentry/react";
import {getBestName, getCategoryKey, getExternalID, RE_CATEGORY_PREFIXED_ID} from "./utils";
import {interpolate} from "../markdown/interpolation";
import { DEV } from "./configMgr";

// Throttle missing-dataset Sentry reports: collect unique dsKeys and flush once per second.
// Prevents N console.error + Sentry event calls when N entities share the same missing dataset.
const _missingDsKeys = new Set();
let _missingDsFlushTimer = null;
const _reportMissingDataset = (dsKey) => {
  _missingDsKeys.add(dsKey);
  if (!_missingDsFlushTimer) {
    _missingDsFlushTimer = setTimeout(() => {
      _missingDsFlushTimer = null;
      const keys = Array.from(_missingDsKeys);
      _missingDsKeys.clear();
      console.warn(`Dataset(s) missing from frontend config: ${keys.join(', ')}`);
      Sentry.captureMessage(`Missing frontend datasets: ${keys.join(', ')}`, {
        level: "warning",
        fingerprint: ["missing-frontend-datasets"],
        extra: { missingKeys: keys },
      });
    }, 1000);
  }
};

const splitID = (id) => {
  if (!id) return ["", ""];
  const colon = id.indexOf(':');
  return [id.substring(0, colon), id.substring(colon + 1)];
}
const DS_WIKIPEDIA = 'wikipedia';
const NO_WIKIPEDIA = /^(UCI|CID|S?CHEMBL|.{50,})/i;
// Map aliases' recognizable prefixes/patterns into dataset-native references
// Anything in an alias list matching a dataset's specified id_format pattern will transformed
const matchExternalID = (alias, datasets, category = null, baseCategory = null) => {
  const matches = [];
  datasets.forEach(ds => {
    const id_format = (ds.categories
        && (ds.categories[category] && ds.categories[category].id_format
          || ds.categories[baseCategory] && ds.categories[baseCategory].id_format))
      || ds.id_format;
    if (alias.startsWith(`${ds.id}:`)) {
      alias = alias.substring(ds.id.length + 1);
    }
    const [format, flags] = id_format.slice(-2) === "/i"
      ? [id_format.slice(0, -2), "i"]
      : [id_format, ""];
    try {
      const pattern = new RegExp(`^${format}$`, flags);
      if (pattern.test(alias)) {
        const replacement = `${ds.id_replace || "$1"}`;
        const externalID = alias.replace(pattern, replacement);
        if (externalID) {
          matches.push([ds, externalID]);
        }
      }
    } catch (error) {
      console.warn(`Bad dataset id format regular expression for '${ds.id}': "${ds.id_format}" (${error})`);
    }
  });
  return matches;
};
export const createSourceLink = (entity, datasets = {}, overrideExternalID = null, overrideDSKey = null, overrideCategory = null) => {
  // Publication nodes reached via entrezgene edges carry source_id=entrezgene:{gene_id},
  // which resolves to a gene bibliography page instead of the PubMed article.
  // Prefer the entity's own pubmed: id in that case.
  const isPubmedPublication = entity.category === 'publication' && entity.id?.startsWith('pubmed:');
  const primaryID = (!isPubmedPublication && entity.source_id) ? entity.source_id : entity.id;
  const [primaryDSKey, primaryExternalID] = splitID(primaryID) || splitID(entity.id);
  let externalID = overrideExternalID || primaryExternalID;
  const dsKey = overrideDSKey || primaryDSKey;
  const dataset = datasets[dsKey];
  if (!entity) {
    console.error("Entity not provided for source link", dataset, externalID);
    return null;
  }
  // Categories which support wikipedia links are identified in datasets.yml, wikipedia section
  if (!dataset) {
    if (dsKey) {
      // Batch reports: N entities with the same missing dataset key send one Sentry event, not N.
      _reportMissingDataset(dsKey);
    }
    return null;
  }
  if (!dataset.categories && dsKey !== "smiles" && dsKey !== "query") {
    if (dsKey !== DS_WIKIPEDIA) {
      DEV && console.warn(`No dataset categories found while resolving source link ${dsKey}:${externalID} (${entity.category})`, dataset);
    }
    return createWikipediaLink(entity, datasets, overrideExternalID);
  }
  const category = overrideCategory || entity.category;
  const baseCategory = overrideCategory || entity.baseCategory;

  // Look for dataset:<key>:dataset_id or <key>:<id> where key is a known category
  let info = null;
  if (RE_CATEGORY_PREFIXED_ID.test(externalID)) {
    const key = getCategoryKey(externalID);
    externalID = getExternalID(externalID);
    info = dataset.categories[key];
  } else if (/^[a-z][-a-z]+:\S/.test(externalID)) {
    const key = externalID.split(':')[0];
    if (dataset.categories && dataset.categories[key]) {
      externalID = externalID.slice(key.length + 1);
      info = dataset.categories[key];
    }
  }
  if (info === null) {
    info = dataset.categories[category] || dataset.categories[baseCategory];
  }
  if (!info || !info.url) {
    if (!dataset.name || !dataset.url) {
      console.warn(`No metadata found for dataset '${dataset.name}' category '${category}'`);
    }
    if (dsKey === "wikipedia") {
      // Not supported, or not likely to be found in wikipedia
      return null;
    }
    return {
      title: (info && info.name) || dataset.name,
      url: (info && info.url) || dataset.url,
      externalID, dataset,
    }
  }
  const props = {...entity};
  const eid = encodeURIComponent(externalID);
  const url = info.url
              ? interpolate(info.url, {...props, id: info.url.indexOf("urlEncode") === -1 ? eid : externalID}) : null;
  const url2 = info.url2 && (!info.pattern2 || new RegExp(info.pattern2, "g").test(externalID))
               ? interpolate(info.url2, {...props, id: info.url.indexOf("urlEncode") === -1 ? eid : externalID}) : null;
  return {
    title: info.name || dataset.name, url,
    description: dataset.description,
    title2: info.name2 ? info.name2 : null, url2,
    externalID,
    dataset: dsKey,
  }
};
const createWikipediaLink = (entity, datasets, term = null) => {
  const bestName = term || getBestName(entity);
  if (!bestName) {
    return null;
  }
  if (NO_WIKIPEDIA.test(bestName)) {
    return null;
  }
  const dataset = datasets[DS_WIKIPEDIA];
  if (!(dataset.categories[entity.category] || dataset.categories[entity.baseCategory])) {
    return null;
  }
  const searchTerm = term || bestName.replace(' ', '_');
  return createSourceLink(entity, datasets, searchTerm, DS_WIKIPEDIA);
};


// generate "green" links
export const generateExternalLinks = (entity, datasets) => {
  // dataset:dataset_id; uses edge_url
  // dataset:<subtype>:dataset_id; uses <subtype>.url from 'categories' block of datasets.yml
  if (!datasets) {
    console.warn("Datasets is empty");
    return [];
  }

  if (entity.aliases && entity.aliases.length > 10000) {
    console.warn(`Way too many aliases for ${entity.id} (${entity.aliases.length}), probably an error`)
    entity.aliases = entity.aliases.slice(0, 10000)
  }
  if (entity.unmapped || entity.id?.startsWith("smiles:")) {
    return [];
  }

  const linkURLs = new Set();
  const addLink = (links_array, link) => {
    if (!link || !links_array) {
      return null;
    }
    // avoid duplicate links
    if (!linkURLs.has(link.url)) {
      linkURLs.add(link.url);
      links_array.push(link);
    }
    if (link.title2 && link.url2 && !linkURLs.has(link.url2)) {
      linkURLs.add(link.url2);
      links_array.push({...link, title: link.title2, url: link.url2})
    }
    return link;
  };
  const links = [];
  const primaryLink = createSourceLink(entity, datasets);
  const allLinks = [];
  addLink(allLinks, primaryLink);
  const category = entity.category;
  const baseCategory = entity.baseCategory;
  try {
    const potentialLinks = (entity.aliases || []).slice();
    if (category === 'patent') {
      const [_, externalID] = splitID(entity.source_id || entity.id);
      potentialLinks.forEach(alias => {
        if (!alias.startsWith(externalID)) {
          addLink(allLinks, createSourceLink(entity, datasets, alias, "patents"));
        }
      });
    }
    if (entity.title) {
      potentialLinks.push(entity.title);
    }
    if (entity.source_id) {
      potentialLinks.push(entity.source_id);
    }
    if (entity.ref_id) {
      potentialLinks.push(entity.ref_id);
    }
    if (primaryLink?.title !== datasets.wikipedia?.name) {
      // Include a wikipedia link where appropriate
      addLink(allLinks, createWikipediaLink(entity, datasets));
    }
    potentialLinks.forEach(alias => {
      const [cat, baseCat] = alias === entity.ref_id ? [entity.ref_category, entity.ref_category] : [category, baseCategory];
      const filteredDatasets = Object.values(datasets)
        .filter(v => v && v.id_format && v.categories && (v.categories[cat] || v.categories[baseCat]))
        // Omit google links for schembl-only patents
        .filter(v => entity.source !== "schembl" && v.id !== "patents");
      const matches = matchExternalID(alias, filteredDatasets, cat, baseCat);
      matches.forEach(match => {
        const [ds, externalID] = match;
        if (ds.id !== DS_WIKIPEDIA) {
          // May want to filter out pubchem/schembl patent "aliases"
          addLink(allLinks, createSourceLink(entity, datasets, externalID, ds.id, baseCat));
        }
      });
    });
    return allLinks;
  } catch (error) {
    console.error(`Error generating external links for ${entity.id}`, error);
    return links;
  }
};

// Limited markdown rendering: generate links from markdown links
export const generateLinksFromMarkdown = ({text = "", referencedEdges = {}, asString = false}) => {
  const s = text;
  let lastEndIndex = 0;
  const RE = /\[([^\]]+)]\(([^)]+)\)/g;
  const parts = [];
  let match;
  let key = 0;
  while ((match = RE.exec(s)) != null) {
    const unmatched = s.substring(lastEndIndex, match.index);
    if (unmatched !== "") {
      parts.push(asString ? unmatched : (<span key={++key}>{unmatched}</span>));
    }
    parts.push(
      asString ? `[${match[1]}](${match[2]})` : (<a key={++key} href={match[2]} target="_blank">{match[1]}</a>));
    lastEndIndex = match.index + match[0].length;
  }
  if (lastEndIndex < s.length) {
    parts.push(asString ? s.substring(lastEndIndex) : (<span key={++key}>{s.substring(lastEndIndex)}</span>));
    if (asString) {
      parts.push("\n");
      Object.entries(referencedEdges).forEach(([id, url]) => {
        parts.push(`[${id}]: ${url}\n`)
      });
    }
  }
  return asString ? parts.join("") : parts;
};
